import type { RateLimitReason } from "@better-ccflare/types";

export type ErrorSeverity = "warning" | "error";

export interface ErrorMeta {
	title: string;
	description: string;
	suggestion: string;
	severity: ErrorSeverity;
}

export interface ErrorContext {
	provider?: string | null;
	otherAccountsAvailable?: boolean;
}

const KNOWN_ERROR_META: Record<
	Exclude<RateLimitReason, "model_fallback_429">,
	ErrorMeta
> = {
	upstream_429_with_reset: {
		title: "Provider rate limit",
		description: "The upstream provider returned 429 with a known reset time.",
		suggestion: "The account will recover automatically at the reset time.",
		severity: "warning",
	},
	upstream_429_no_reset_probe_cooldown: {
		title: "Provider rate limit (no reset)",
		description:
			"The upstream provider returned 429 without a reset header; entering probe cooldown.",
		suggestion:
			"Cooldown defaults to 60s. Set `CCFLARE_DEFAULT_COOLDOWN_NO_RESET_MS` in your environment to change it.",
		severity: "warning",
	},
	upstream_429_no_reset_default_5h: {
		title: "Provider rate limit (legacy 5h ban)",
		description:
			"Legacy ban from ccflare ≤ v3.5.x. No longer emitted by current code.",
		suggestion: "Historical record — no action needed.",
		severity: "warning",
	},
	all_models_exhausted_429: {
		title: "All fallback models rate-limited",
		description: "Every fallback model also returned 429.",
		suggestion: "Wait for cooldown, or add more diverse fallback models.",
		severity: "error",
	},
	upstream_529_overloaded_with_reset: {
		title: "Provider overload",
		description:
			"The upstream provider returned 529 (overloaded) with a Retry-After header. Account temporarily cooled down for that duration.",
		suggestion:
			"No action needed — the account will recover automatically. Traffic will shift to other configured accounts in the meantime.",
		severity: "warning",
	},
	upstream_529_overloaded_no_reset: {
		title: "Provider overload (no Retry-After)",
		description:
			"The upstream provider returned 529 (overloaded) without a Retry-After header; entering probe cooldown. This also covers mid-stream overloaded_error detections, which never carry a Retry-After header — HTTP headers were already sent before the error occurred in that path.",
		suggestion:
			"Cooldown defaults to 10s and pairs with a single-flight recovery probe (only one request re-probes the account once it expires, as long as another account is available to defer to — if every account is currently suppressed, the request runs ungated instead). Set `CCFLARE_OVERLOAD_COOLDOWN_MS` in your environment to change it.",
		severity: "warning",
	},
	upstream_5xx_server_error: {
		title: "Provider server error (5xx)",
		description:
			"Upstream returned 500/502/503/504; retried in place, then benched briefly and failed over. The account's own quota is fine — the provider (or the organization behind the account) failed to serve the request.",
		suggestion:
			"No action needed — the request was retried once on the same account and then moved to another one. The bench defaults to 60s, or a shorter upstream Retry-After when one is sent. Set `CCFLARE_SERVER_ERROR_COOLDOWN_MS` to change it, or `CCFLARE_SERVER_ERROR_RETRY_ENABLED=false` to forward these responses straight to the client again.",
		severity: "warning",
	},
	out_of_credits: {
		title: "Account out of credits",
		description:
			"Anthropic returned 429 with `overage-disabled-reason: out_of_credits` — credits/overage for a specific model/beta (e.g. context-1m) is depleted. This is model-scoped, so the account stays in rotation for other models and the request fails over automatically.",
		suggestion:
			"Top up the account's credits or raise its overage allowance. Meanwhile, traffic for other models continues to use this account, and the rejected model shifts to other accounts.",
		severity: "error",
	},
	windowless_429: {
		title: "Request rejected (no rate-limit window)",
		description:
			"Anthropic returned 429 with `x-should-retry: true` and no rate-limit " +
			"window at all — no `retry-after` and no `anthropic-ratelimit-*` " +
			"header. Measured behaviour is that this is scoped to the request, not " +
			"the account: the same account keeps serving other requests in the same " +
			"second, while every other account rejects this particular request the " +
			"same way. The account was NOT benched and stays in rotation. The " +
			"request itself was tried on the remaining accounts and, because they " +
			"all reject it identically, still failed for the client.",
		suggestion:
			"No action needed on the accounts — they are still routable and normal " +
			"traffic is unaffected. Upstream rejected this specific request for " +
			"reasons narrower than the account, so neither retrying it nor adding " +
			"accounts helps; the client typically has to start a new request. A " +
			"steady stream of these points at the requests themselves (session " +
			"start-up, unusually large payload) rather than at your accounts.",
		severity: "warning",
	},
	extra_usage_exhausted: {
		title: "Extra usage credits depleted",
		description:
			"Anthropic returned 400 invalid_request_error: this OAuth account's " +
			"extra-usage credit balance is $0. Anthropic bills third-party-app " +
			"traffic on Claude OAuth accounts from a separate extra-usage pool, " +
			"not the plan's included quota — Haiku requests may still succeed " +
			"since routing/exemption can differ by model.",
		suggestion:
			"Add credits or enable auto-reload at claude.ai/settings/usage. This " +
			"is an Anthropic billing state, not a proxy failure — the account " +
			"stays in rotation and the request is passed through unchanged.",
		severity: "error",
	},
	upstream_402_out_of_credits: {
		title: "Out of credits upstream",
		description:
			"The upstream provider returned HTTP 402: this account's balance, " +
			"included monthly usage or reseller credits are exhausted (e.g. " +
			"Mistral, OpenRouter, NanoGPT). The account stays routable — it is " +
			"not benched — and the request failed over to the next account.",
		suggestion:
			"Top up the account (or wait for the billing month to roll over). " +
			"This is a funding state upstream, not a proxy failure: the account " +
			"returns to service on its own as soon as credits are available.",
		severity: "error",
	},
	org_permission_denied: {
		title: "Organization forbids this account",
		description:
			"Anthropic returned 403 `permission_error`: this account's " +
			"organization does not allow the request — OAuth disabled org-wide, " +
			"or Claude Code subscription access turned off by an admin. Nothing " +
			"is wrong with the account's quota, but it cannot serve any request, " +
			"so it was benched and the request failed over to the next account.",
		suggestion:
			"Ask the organization's admin to enable Claude Code access, or remove " +
			"this account from the pool. The bench is not time-bounded upstream: " +
			"it will expire and a single recovery probe will rediscover the same " +
			"403 until the org setting actually changes, so a steady trickle of " +
			"these is expected while the account stays configured.",
		severity: "error",
	},
};

function getModelFallbackMeta(context?: ErrorContext): ErrorMeta {
	const provider = context?.provider ?? null;
	const otherAccountsAvailable = context?.otherAccountsAvailable;

	const isOAuthOnlyProvider = provider === "anthropic" || provider === "codex";

	const suggestion = isOAuthOnlyProvider
		? "No action needed — Claude/Codex accounts only serve their native models, so the proxy will use the next account until this one recovers."
		: 'To retry on the same account before failing over, open this account\'s More actions → Model Mappings and add comma-separated alternates (e.g. "primary, fallback-1").';

	const baseDescription =
		"This account hit a 429 with only one model configured, so the proxy failed over to the next account in priority order.";

	if (otherAccountsAvailable === false) {
		return {
			title: "Account rate-limited — no in-account fallback",
			description: `No other accounts are available — requests will fail until this account recovers. ${baseDescription}`,
			suggestion,
			severity: "error",
		};
	}

	return {
		title: "Account rate-limited — no in-account fallback",
		description: baseDescription,
		suggestion,
		severity: "warning",
	};
}

export function getErrorMeta(code: string, context?: ErrorContext): ErrorMeta {
	if (code === "model_fallback_429") {
		return getModelFallbackMeta(context);
	}
	if (code in KNOWN_ERROR_META) {
		return KNOWN_ERROR_META[code as keyof typeof KNOWN_ERROR_META];
	}
	return {
		title: code || "Unknown error",
		description: "No additional context is available for this error code.",
		suggestion: "Check the server logs or the original request for details.",
		severity: "error",
	};
}
